<script setup lang="ts">
  import { type HTMLAttributes, computed } from 'vue'
  import {
    TooltipContent,
    type TooltipContentEmits,
    type TooltipContentProps,
    TooltipPortal,
    TooltipArrow,
    useForwardPropsEmits
  } from 'radix-vue'
  import { cn } from '@/lib/utils'

  export type TooltipVariant = 'default' | 'secondary' | 'error' | 'success' | 'warning'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<
      TooltipContentProps & {
        class?: HTMLAttributes['class']
        variant?: TooltipVariant
        showArrow?: boolean
        arrowClassName?: string
      }
    >(),
    {
      sideOffset: 4,
      variant: 'default',
      showArrow: false
    }
  )

  const emits = defineEmits<TooltipContentEmits>()

  const delegatedProps = computed(() => {
    const { class: _, variant: __, showArrow: ___, arrowClassName: ____, ...delegated } = props

    return delegated
  })

  const variantStyles = computed(() => {
    const baseStyles = `z-50 overflow-hidden rounded-md border px-3 py-1.5 text-sm
      shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out
      data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95
      data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2
      data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`

    switch (props.variant) {
      case 'secondary':
        return `${baseStyles} bg-black text-white border-black`
      case 'error':
        return `${baseStyles} bg-destructive text-destructive-foreground border-destructive`
      case 'success':
        return `${baseStyles} bg-success text-success-foreground border-success`
      case 'warning':
        return `${baseStyles} bg-warning text-warning-foreground border-warning`
      default:
        return `${baseStyles} bg-popover text-popover-foreground border-border`
    }
  })

  const arrowStyles = computed(() => {
    switch (props.variant) {
      case 'secondary':
        return 'fill-black'
      case 'error':
        return 'fill-destructive'
      case 'success':
        return 'fill-success'
      case 'warning':
        return 'fill-warning'
      default:
        return 'fill-popover'
    }
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <TooltipPortal>
    <TooltipContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="cn(variantStyles, props.class)"
    >
      <slot />
      <TooltipArrow
        v-if="props.showArrow"
        :class="cn(arrowStyles, props.arrowClassName)"
      />
    </TooltipContent>
  </TooltipPortal>
</template>
